"use client";
import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { hasWindow } from "@lib/utils/hasWindow";
import type { Dispatch, SetStateAction } from "react";
import { createContext, useCallback, useEffect, useState } from "react";
import { fetchStores } from "./fetch-store";
import { normalizeStore } from "./normalize-store";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { storeLocatorPage } from "@lib/constants/pagePaths";
import { activeSiteConfig } from "@config/site/site-config";
import { restrictedCountries } from "@lib/constants/restrictedCountries";
import { filterValueStoreType } from "./filterValues";
import { useStore } from "@store/store";
import { ShippingMethodTypes } from "@components/commerce/checkout/types";
import { useSession } from "next-auth/react";
import { useLocale } from "next-intl";
import { usePathname } from "i18n/routing";

export const dayOfWeek = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export enum MapState {
    Unmounted,
    Mounted,
    Ready,
    Completed,
}

export type DayOfWeek = (typeof dayOfWeek)[number];
export type DayOfWeekLabel = {
    [dayOfWeek: string]: string;
};
export type Hours = {
    open: string;
    close: string;
};
export type WalkInHours = {
    [dayOfWeek in DayOfWeek]: Hours;
};

export interface StoreData {
    storeId: string;
    name: string;
    street1?: string;
    street2?: string;
    city: string;
    postalCode: string;
    phone: string;
    countryCode: string;
    email: string;
    storeTypeId: string;
    categories: string[];
    latitude: number;
    longitude: number;
    timeZone: string;
    walkInHours: WalkInHours;
    availability?: any;
    supplyChannel?: any[];
    pickupInStore?: boolean;
    distributionChannel?: any[];
    region?: string;
    distanceFromClient?: string | null;
    carrier?: string;
    parcelTypeCode?: string;
}

type StoreDataIndex = {
    [storeId: string]: StoreData;
};

type ClientLocation = {
    lat: number;
    lng: number;
};

type ContextProps = {
    selectedStoreId: string;
    setSelectedStoreId: Dispatch<SetStateAction<string>>;
    highlightSelectedStore: boolean;
    setHighlightSelectedStore: Dispatch<SetStateAction<boolean>>;
    searchResult: google.maps.GeocoderResult[];
    setSearchResult: Dispatch<SetStateAction<google.maps.GeocoderResult[]>>;
    lastSelect: number;
    setLastSelect: Dispatch<SetStateAction<number>>;
    lastQuery: string;
    setLastQuery: Dispatch<SetStateAction<string>>;
    clientLocation?: ClientLocation;
    setClientLocation: Dispatch<SetStateAction<ClientLocation>>;
    storeData: StoreData[];
    setStoreData: Dispatch<SetStateAction<any>>;
    storeDataIndex: StoreDataIndex;
    setStoreDataIndex: Dispatch<SetStateAction<StoreDataIndex>>;
    selectedStore: () => StoreData | undefined;
    visibleStores: string[];
    setVisibleStores: Dispatch<SetStateAction<string[]>>;
    dayOfWeekLabels: DayOfWeekLabel;
    validStoreId: (storeId: string) => boolean;
    mapBounds: google.maps.LatLngBounds;
    setMapBounds: Dispatch<SetStateAction<google.maps.LatLngBounds>>;
    storesWithStock: any[];
    setStoresWithSTock: Dispatch<SetStateAction<any[]>>;
    isClickAndCollect: boolean;
    setIsClickAndCollect: Dispatch<SetStateAction<boolean>>;
    isPickUpMethod?: boolean;
    setIsPickUpMethod?: Dispatch<SetStateAction<boolean>>;
    showPartnerStores: boolean;
    setShowPartnerStores: Dispatch<SetStateAction<boolean>>;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    isLoading: boolean;
    getShouldShowStore?: (id: string) => boolean;
    mapState: MapState;
    setMapState: Dispatch<SetStateAction<MapState>>;
    shouldRestrictCountry: boolean;
    filterDistance?: { value: string | number; label: string };
    setFilterDistance: Dispatch<SetStateAction<{ value: string | number; label: string }>>;
    storeTypeFilter?: { value: string; label: string };
    setStoreTypeFilter: Dispatch<SetStateAction<{ value: string; label: string }>>;
    storesInList?: number;
    setStoresInList: Dispatch<SetStateAction<number>>;
    setStoreDataForType: Dispatch<SetStateAction<any>>;
    setCurrentLocation: () => void;
    showStoreBadge: boolean;
};

export const Controller = createContext<ContextProps>({
    selectedStoreId: "",
    setSelectedStoreId: () => {},
    highlightSelectedStore: false,
    setHighlightSelectedStore: () => {},
    searchResult: [],
    setSearchResult: () => {},
    lastSelect: 0,
    setLastSelect: () => {},
    lastQuery: "",
    setLastQuery: () => {},
    clientLocation: undefined,
    setClientLocation: () => {},
    storeData: [],
    setStoreData: () => {},
    storeDataIndex: {},
    selectedStore: () => undefined,
    visibleStores: [],
    setVisibleStores: () => {},
    dayOfWeekLabels: {},
    validStoreId: () => false,
    mapBounds: undefined,
    setMapBounds: () => {},
    storesWithStock: [],
    setStoresWithSTock: () => {},
    isClickAndCollect: false,
    setIsClickAndCollect: () => {},
    isPickUpMethod: false,
    setIsPickUpMethod: () => {},
    setStoreDataIndex: () => {},
    showPartnerStores: false,
    setShowPartnerStores: () => {},
    setIsLoading: () => {},
    isLoading: false,
    getShouldShowStore: undefined,
    mapState: MapState.Unmounted,
    setMapState: () => {},
    // Canada and US market will have country restriction for Store Locator's search and stores
    shouldRestrictCountry: false,
    filterDistance: null,
    setFilterDistance: () => {},
    storeTypeFilter: filterValueStoreType[0],
    setStoreTypeFilter: () => {},
    storesInList: 0,
    setStoresInList: () => {},
    setStoreDataForType: () => {},
    setCurrentLocation: () => {},
    showStoreBadge: true,
});

type StoreLocatorProviderProps = {
    children: JSX.Element;
    initStores?: StoreData[];
    shippingMethodKey?: ShippingMethodTypes;
};

export const Provider = ({
    children,
    initStores = [],
    shippingMethodKey,
}: StoreLocatorProviderProps) => {
    const { getMultiple } = useMicrocopy();
    const locale = useLocale();
    const route = usePathname();

    const { data: session, status } = useSession();
    const [storeData, setStoreData] = useState<StoreData[]>(initStores);
    const [storesWithStock, setStoresWithSTock] = useState<any>([]);
    const [isClickAndCollect, setIsClickAndCollect] = useState<false>(false);
    const [isPickUpMethod, setIsPickUpMethod] = useState<false>(false);
    const [storeDataIndex, setStoreDataIndex] = useState<StoreDataIndex>({});
    const [visibleStores, setVisibleStores] = useState<string[]>([]);
    const [selectedStoreId, setSelectedStoreId] = useState<string>();
    const [highlightSelectedStore, setHighlightSelectedStore] = useState<boolean>(false);
    const [searchResult, setSearchResult] = useState<google.maps.GeocoderResult[]>();
    const [lastSelect, setLastSelect] = useState<number>(0);
    const [lastQuery, setLastQuery] = useState<string>("");
    const [clientLocation, setClientLocation] = useState<google.maps.LatLngLiteral>();
    const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds>();
    const [showPartnerStores, setShowPartnerStores] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [mapState, setMapState] = useState(MapState.Unmounted);
    const [filterDistance, setFilterDistance] = useState<{ value: number | string; label: string }>(
        null
    );
    const [storesInList, setStoresInList] = useState<number>(0);
    const [storeTypeFilter, setStoreTypeFilter] = useState<{ value: string; label: string }>(
        filterValueStoreType[0]
    );
    const { country } = activeSiteConfig;

    const setCCStoreData = useStore((state) => state.setStoreData);
    const setParcelShopData = useStore((state) => state.setParcelShopData);

    const microcopies = (() => {
        return (
            getMultiple(CommonCMS.page, [
                ClickAndCollect.workingDays.sunday,
                ClickAndCollect.workingDays.monday,
                ClickAndCollect.workingDays.tuesday,
                ClickAndCollect.workingDays.wednesday,
                ClickAndCollect.workingDays.thursday,
                ClickAndCollect.workingDays.friday,
                ClickAndCollect.workingDays.saturday,
            ]) ?? {}
        );
    })();
    const dayOfWeekLabels = {
        sun: microcopies[ClickAndCollect.workingDays.sunday],
        mon: microcopies[ClickAndCollect.workingDays.monday],
        tue: microcopies[ClickAndCollect.workingDays.tuesday],
        wed: microcopies[ClickAndCollect.workingDays.wednesday],
        thu: microcopies[ClickAndCollect.workingDays.thursday],
        fri: microcopies[ClickAndCollect.workingDays.friday],
        sat: microcopies[ClickAndCollect.workingDays.saturday],
    };

    const shouldRestrictCountry = restrictedCountries.find((c) => c === country) !== undefined;

    const selectedStore = useCallback(() => {
        return selectedStoreId !== null ? storeDataIndex[selectedStoreId] : undefined;
    }, [selectedStoreId, storeDataIndex]);

    const validStoreId = (storeId: string) => /^\d{10}$/.test(storeId);

    useEffect(() => {
        if (visibleStores.length > 0 && validStoreId(lastQuery)) {
            setSelectedStoreId(lastQuery);
        }
    }, [visibleStores, lastQuery]);

    useEffect(() => {
        setStoreDataIndex(
            storeData.reduce((acc, store) => {
                acc[store.storeId] = store;
                return acc;
            }, {})
        );
    }, [storeData, visibleStores]);

    useEffect(() => {
        const storeLocatorRoutes = Object.values(storeLocatorPage);
        // Fetch all stores only when is store locator page, otherwise we will get the stores from the props, fetched on the parent component
        if (storeLocatorRoutes.includes(String(route))) {
            setIsLoading(true);
            // TODO: Replace this with React Query. Be wary of initStores, I don't know what it's used for.
            if (status === "authenticated") {
                fetchStores(country, true, session.accessToken)
                    .then((data) => {
                        setStoreData(normalizeStore(data, locale));
                    })
                    .catch((error) => {
                        Logger.error(ServiceType.WEB, "Error fetching stores", {
                            tag: LogTag.STORES,
                            error,
                        });
                    })
                    .finally(() => setIsLoading(false));
            }
        }
    }, [status]);

    const setCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                setClientLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
            });
        }
    };

    useEffect(() => {
        if (!hasWindow) {
            return;
        }
        setCurrentLocation();
    }, []);

    const getShouldShowStore = (id: string) =>
        showPartnerStores || storeDataIndex[id]?.storeTypeId === "ECCO";

    const setStoreDataForType =
        shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP ? setParcelShopData : setCCStoreData;

    const showStoreBadge = shippingMethodKey !== ShippingMethodTypes.PARCEL_SHOP;

    return (
        <Controller.Provider
            value={{
                mapState,
                setMapState,
                selectedStoreId,
                setSelectedStoreId,
                storesWithStock,
                setStoresWithSTock,
                isClickAndCollect,
                setIsClickAndCollect,
                isPickUpMethod,
                setIsPickUpMethod,
                highlightSelectedStore,
                setHighlightSelectedStore,
                searchResult,
                setSearchResult,
                showPartnerStores,
                setShowPartnerStores,
                lastSelect,
                setLastSelect,
                lastQuery,
                setLastQuery,
                setIsLoading,
                clientLocation,
                setClientLocation,
                storeData,
                setStoreData,
                storeDataIndex,
                setStoreDataIndex,
                selectedStore,
                visibleStores,
                setVisibleStores,
                dayOfWeekLabels,
                validStoreId,
                mapBounds,
                setMapBounds,
                isLoading,
                getShouldShowStore,
                shouldRestrictCountry,
                filterDistance,
                setFilterDistance,
                storeTypeFilter,
                setStoreTypeFilter,
                storesInList,
                setStoresInList,
                setStoreDataForType: setStoreDataForType,
                setCurrentLocation,
                showStoreBadge,
            }}
        >
            {children}
        </Controller.Provider>
    );
};
